/**
 * Inline stroke icons. Bundling a full icon package for a dozen glyphs costs
 * more than it saves; these inherit `currentColor` and size from a single
 * prop, so they pick up whatever the surrounding text is doing.
 */
type Props = { size?: number; className?: string };

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

export const IconSearch = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

export const IconHome = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5.5 9.5V20h13V9.5" />
  </svg>
);

export const IconStar = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="m12 3.5 2.6 5.4 5.9.85-4.25 4.15 1 5.9L12 17l-5.25 2.8 1-5.9L3.5 9.75l5.9-.85z" />
  </svg>
);

export const IconStarFilled = ({ size = 18, className }: Props) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="m12 3.5 2.6 5.4 5.9.85-4.25 4.15 1 5.9L12 17l-5.25 2.8 1-5.9L3.5 9.75l5.9-.85z" />
  </svg>
);

export const IconBriefcase = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <rect x="3" y="7.5" width="18" height="12.5" rx="2.5" />
    <path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5" />
    <path d="M3 12h18" />
  </svg>
);

export const IconList = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
  </svg>
);

export const IconWallet = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a2 2 0 0 1 2 2v1" />
    <rect x="3" y="7.5" width="18" height="11.5" rx="2.5" />
    <circle cx="16.5" cy="13.25" r="1.15" fill="currentColor" stroke="none" />
  </svg>
);

export const IconReport = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M6 20V11M12 20V4.5M18 20v-6" />
    <path d="M3.5 20h17" />
  </svg>
);

export const IconUser = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="8.5" r="3.75" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </svg>
);

export const IconBell = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M18 15.5V10a6 6 0 1 0-12 0v5.5L4.5 18h15z" />
    <path d="M10 21h4" />
  </svg>
);

export const IconClose = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const IconChevronRight = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />
  </svg>
);

export const IconArrowUpRight = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M7.5 16.5 16.5 7.5M9 7.5h7.5V15" />
  </svg>
);

export const IconArrowDownLeft = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M16.5 7.5 7.5 16.5M15 16.5H7.5V9" />
  </svg>
);

export const IconPlus = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconSun = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4" />
  </svg>
);

export const IconMoon = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M20 13.5A8 8 0 0 1 10.5 4a8 8 0 1 0 9.5 9.5z" />
  </svg>
);

export const IconRefresh = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M20 12a8 8 0 1 1-2.5-5.8" />
    <path d="M20 4v4.5h-4.5" />
  </svg>
);

export const IconBank = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M3.5 9.5 12 4.5l8.5 5" />
    <path d="M5.5 9.5V17M10 9.5V17M14 9.5V17M18.5 9.5V17" />
    <path d="M3.5 20h17" />
  </svg>
);

export const IconInfo = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

export const IconDownload = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M12 4v10.5M8 11l4 4 4-4" />
    <path d="M4.5 19.5h15" />
  </svg>
);

export const IconCopy = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <rect width="13" height="13" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </svg>
);

export const IconCheck = ({ size = 18, className }: Props) => (
  <svg {...base(size)} className={className} aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
